import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import {
  ActionDefinition,
  BasicStatementV2,
  Cadence,
  ClientGQL,
  ClientGQLDisplay,
  CollectionsApiService,
  CollectionsSequence,
  CommunicationRecipientRow,
  DynamicField,
  ExclusionStatusUpdate,
  GraphQl,
  MarshallUpdateCadenceRequest,
  MarshallUpdateSequenceRequest,
  NextStatement,
  RecipientOutstandingAggregates,
  SendStatementPayload,
  SequenceAggregate,
  SmartStatementActionDefinitionJson,
  SmartStatementBucket,
  SmartStatementBucketSetting,
  SmartStatementRecipient,
  StaffGQL,
  Statement,
  StatementStatus,
  UpdateCollectionsSettingsPayload,
} from '@anduin/api';
import { AuthService, LaunchDarklyFlag, LaunchDarklyState } from '@anduin/auth';
import { CollectionsMode, ConfigState } from '@anduin/config';
import {
  GqlPaginationVariables,
  Loadable,
  LoadableArrayLoadStrategy,
  LoadableHelper,
  LoadableStatus,
  Logger,
  ObjectTraversalPath,
  ObjectTraversalResult,
  Page,
  PagedData,
  PagedDataModel,
  areStringsEqualIgnoreCase,
  getLoadableArrayLoadingState,
  getLoadedResults,
  getNestedValue,
  ignoreNullish,
  isEmpty,
  isLoaded,
  moveArrayItem,
  removeValuesByField,
  replaceArrayItem,
  setLoadableArrayInState,
  setLoadableInState,
} from '@anduin/shared';
import { Action, Selector, State, StateContext, Store, createSelector } from '@ngxs/store';
import moment from 'moment';
import { MonoTypeOperatorFunction, Observable, combineLatest, defer, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AbstractStatementRecipient, StatementRecipient, StatementRecipientModel } from '../models';
import {
  ApproveStatementComplete,
  ClearCollectionsPageData,
  ClearExcludedClientsState,
  Clients_UpdateClientExclusion,
  Collections_ApproveStatement,
  Collections_SkipStatement,
  Collections_UndoSkipStatement,
  Collections_UpdateRecipientExclusion,
  GetActiveCadences,
  GetAlreadyExcludedCollectionsClientsTotal,
  GetAutoApproveSetting,
  GetClientNextStatement,
  GetCollectionsRecipients,
  GetCollectionsSettings,
  GetEmailSettings,
  GetExcludedStatementRecipientIds,
  GetExcludedStatementRecipients,
  GetMessageSettings,
  GetMessageVariables,
  GetOutstandingSummaryByRecipient,
  GetRecipientPreviousStatements,
  GetSequenceAggregatesByCadenceId,
  GetSequenceBySequenceId,
  GetStaff,
  GetThrottlingSettings,
  GetTriggerSettings,
  SearchCollectionsRecipients,
  SearchExcludedStatementRecipients,
  SetSelectedStatementRecipientId,
  SkipStatementComplete,
  SortExcludedStatementRecipients,
  ToggleSelectedRecipientEnabled,
  UndoSkipStatement,
  UndoSkipStatementComplete,
  UpdatableSettingsField,
  UpdateCadence,
  UpdateClientExclusion,
  UpdateCollectionsClientExclusionStatus,
  UpdateCollectionsSettings,
  UpdateMessageSettings,
  UpdateRecipientListStatus,
  UpdateSequence,
} from './common-collections.actions';
import {
  COMMON_COLLECTIONS_STATE_MODEL_DEFAULT,
  CollectionsSettingsStateModel,
  CollectionsStatements,
  CommonCollectionsStateModel,
  CommonCollectionsStateModelPagedExtraParameters,
  DEFAULT_PAGE_RECIPIENT_RESULTS,
  DEFAULT_RECIPIENT_PAGE_LIMIT,
  RecipientSearchResult,
  SequenceWrapper,
  SortOption,
} from './common-collections.model';

export const COMMON_COLLECTIONS_STATE_SLICE = 'commonCollections';

const MAX_CACHED_SEARCHES = 7;
@State<CommonCollectionsStateModel>({
  name: COMMON_COLLECTIONS_STATE_SLICE,
  defaults: COMMON_COLLECTIONS_STATE_MODEL_DEFAULT,
})
@Injectable({
  providedIn: 'root',
})
export class CommonCollectionsState {
  //#region Selectors (and static helpers)

  @Selector([LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2), ConfigState.getConfig('collectionsMode')])
  static getCollectionsMode(isCollectionsV2Enabled: boolean, collectionsMode: CollectionsMode): CollectionsMode {
    return isCollectionsV2Enabled ? collectionsMode : CollectionsMode.CLIENT;
  }

  //#region Recipients

  @Selector()
  static getRawStatementRecipients(state: CommonCollectionsStateModel): StatementRecipientModel[] {
    return state.recipients;
  }

  static getRawStatementRecipient(recipientId: number): (recipients: StatementRecipientModel[]) => StatementRecipientModel {
    return createSelector([CommonCollectionsState.getRawStatementRecipients], (recipients) =>
      recipients.find(
        CommonCollectionsState.isV1Recipient(recipients[0])
          ? ({ client_id }: GraphQl.ClientCadenceType) => client_id === recipientId
          : ({ id }: SmartStatementRecipient) => id === recipientId,
      ),
    );
  }

  @Selector([CommonCollectionsState.getRawStatementRecipients, ConfigState.getConfig('smartStatementHideNonTriggeredInvoices')])
  static getAllStatementRecipients(recipients: StatementRecipientModel[], shouldHideUntriggeredInvoices: boolean): StatementRecipient[] {
    if (recipients == undefined) {
      return recipients as undefined; // doing it this way because it could be null, sue me (plz, I miss my lawyer)
    }

    return recipients.map((recipient) => AbstractStatementRecipient.create(recipient, shouldHideUntriggeredInvoices));
  }

  static getStatementRecipient(id: number): (statementRecipients: StatementRecipient[]) => StatementRecipient {
    return createSelector([CommonCollectionsState.getAllStatementRecipients], (recipients) =>
      recipients.find((recipient) => recipient.id === id),
    );
  }

  @Selector()
  static getClientPagedResults(state: CommonCollectionsStateModel): PagedDataModel<number> {
    return state.pagedRecipientResults;
  }

  /**
   * Selector to get current list of clients for all pages that have been requests
   */
  @Selector([CommonCollectionsState.getAllStatementRecipients, CommonCollectionsState.getClientPagedResults])
  static getStatementRecipients(recipients: StatementRecipient[], recipientPagedResults: PagedDataModel<number>): StatementRecipient[] {
    return recipientPagedResults.pages.reduce((accumulator, page) => {
      const recipientResults = CommonCollectionsState.mapIdsToRecipients(page.data.data, recipients);
      return accumulator.concat(recipientResults);
    }, [] as StatementRecipient[]);
  }

  /**
   * Get the load status of the latest request for clients
   * @param state
   */
  @Selector()
  static getLatestRecipientPageStatus(state: CommonCollectionsStateModel): LoadableStatus {
    const latestPage: Loadable<Page<number>> = state.pagedRecipientResults.pages.reduce(
      (accumulator, current) => (current.data.pageNumber > accumulator.data.pageNumber ? current : accumulator),
      state.pagedRecipientResults.pages?.[0],
    );
    return latestPage ? latestPage.status : LoadableStatus.LOADING;
  }

  /**
   * get the total number of client pages
   * @param state
   */
  @Selector()
  static getTotalRecipientPages(state: CommonCollectionsStateModel): number {
    return state.pagedRecipientResults.totalPages;
  }

  @Selector()
  static getRawRecipientSearchResults(state: CommonCollectionsStateModel): RecipientSearchResult[] {
    return state.recipientSearchResults;
  }

  static getRawRecipientSearchResult(
    term: string,
    partnerName: string,
    sort?: SortOption,
  ): (recipientSearchResults: RecipientSearchResult[]) => RecipientSearchResult {
    const cacheKey = CommonCollectionsState.multiFieldToCacheKey(term, partnerName, CommonCollectionsState.sortToStr(sort));

    return createSelector([CommonCollectionsState.getRawRecipientSearchResults], (searchResults) =>
      searchResults.find((search) => areStringsEqualIgnoreCase(search.term, cacheKey)),
    );
  }

  private static sortToStr(sort?: SortOption): string {
    if (!sort) {
      return ':';
    }

    return `${sort.field}:${sort.direction}`;
  }

  static multiFieldToCacheKey(...fields: string[]): string {
    return fields.join(',');
  }

  static cacheKeyToMultiFieldValue(cacheKey: string): string[] {
    return cacheKey.split(',');
  }

  /**
   * For the given search term, what is the correlating search's status
   * @param term
   */
  static getRecipientSearchLoadable(
    term: string,
    partnerName = '',
    sort: SortOption,
  ): (searchResult: RecipientSearchResult, recipients: StatementRecipient[]) => Loadable<StatementRecipient[]> {
    return createSelector(
      [CommonCollectionsState.getRawRecipientSearchResult(term, partnerName, sort), CommonCollectionsState.getAllStatementRecipients],
      (searchResult, recipients) => {
        if (searchResult?.results == undefined) {
          return undefined;
        }

        return {
          ...searchResult.results,
          data: CommonCollectionsState.mapIdsToRecipients(searchResult.results.data, recipients),
        };
      },
    );
  }

  /**
   * For the given search term, what is the correlating search's status
   * @param term
   */
  static getRecipientSearchStatus(term: string, partnerName = ''): (searchResult: RecipientSearchResult) => LoadableStatus {
    return createSelector(
      [CommonCollectionsState.getRawRecipientSearchResult(term, partnerName)],
      (searchResult) => searchResult?.results?.status || LoadableStatus.DEFAULT,
    );
  }

  @Selector([CommonCollectionsState.getRawRecipientSearchResults])
  static getLatestRecipientSearch(recipientSearchResults: RecipientSearchResult[]): RecipientSearchResult {
    return recipientSearchResults[recipientSearchResults.length - 1];
  }

  @Selector([CommonCollectionsState.getLatestRecipientSearch])
  static getLatestRecipientSearchStatus(latestRecipientSearch: RecipientSearchResult): LoadableStatus {
    return latestRecipientSearch?.results?.status;
  }

  /**
   * Get the recipient results from the search matching the term
   * @param term
   */
  static getRecipientSearchResults(
    term: string,
    partner?: string,
    sort?: SortOption,
  ): (searchResults: Loadable<StatementRecipient[]>) => StatementRecipient[] {
    return createSelector(
      [CommonCollectionsState.getRecipientSearchLoadable(term, partner, sort)],
      (searchResults) => searchResults?.data ?? [],
    );
  }

  @Selector()
  static getSelectedStatementRecipientId(state: CommonCollectionsStateModel): number {
    return state.selectedStatementRecipientId;
  }

  @Selector([CommonCollectionsState.getSelectedStatementRecipientId, CommonCollectionsState.getAllStatementRecipients])
  static getSelectedStatementRecipient(selectedRecipientId: number, recipients: StatementRecipient[]): StatementRecipient {
    return recipients.find(({ id }) => id === selectedRecipientId);
  }

  private static mapIdsToRecipients(clientSearchResults: number[], recipients: StatementRecipient[]): StatementRecipient[] {
    return (clientSearchResults ?? []).map((recipientId) => recipients.find(({ id }) => id === recipientId));
  }

  private static coalesceRecipients(
    currentRecipients: StatementRecipientModel[],
    newRecipients: StatementRecipientModel[],
  ): StatementRecipientModel[] {
    const recipientIds = CommonCollectionsState.mapRecipientsToIds(newRecipients);
    const isCollectionsV1 = CommonCollectionsState.isV1Recipient(currentRecipients[0]);
    const filteredCurrentRecipients = isCollectionsV1
      ? removeValuesByField(currentRecipients as GraphQl.ClientCadenceType[], 'client_id', recipientIds)
      : removeValuesByField(currentRecipients as SmartStatementRecipient[], 'id', recipientIds);

    return filteredCurrentRecipients.concat(
      isCollectionsV1 ? (newRecipients as GraphQl.ClientCadenceType[]) : (newRecipients as SmartStatementRecipient[]),
    );
  }

  private static mapRecipientsToIds(recipients: StatementRecipientModel[]): number[] {
    return recipients.map(
      CommonCollectionsState.isV1Recipient(recipients[0])
        ? ({ client_id }: GraphQl.ClientCadenceType) => client_id
        : ({ id }: SmartStatementRecipient) => id,
    );
  }

  private static addStaffNamesToClients(clients: ClientGQL[], staff: StaffGQL[]): ClientGQLDisplay[] {
    return clients.map((client) => {
      const updateByStaffMember = staff.find(({ id }) => id === client.updateByStaffId);
      return {
        ...client,
        updateByStaffName: `${updateByStaffMember?.firstName || ''} ${updateByStaffMember?.lastName || ''}`.trim(),
      };
    });
  }

  private static isV1Recipient(recipient: StatementRecipientModel): recipient is GraphQl.ClientCadenceType {
    return 'client_id' in (recipient ?? {});
  }

  //#endregion

  //#region Cadences

  @Selector()
  static getFirstCadence(state: CommonCollectionsStateModel): Cadence {
    return state.cadences?.data?.length ? state.cadences.data[0] : undefined;
  }

  @Selector()
  static getCadenceStatus(state: CommonCollectionsStateModel): LoadableStatus {
    return state.cadences.status;
  }

  /**
   * Get the status of the request for the Cadence update
   */
  @Selector()
  static getCadenceUpdateRequestStatus(state: CommonCollectionsStateModel): LoadableStatus {
    return state.cadenceUpdateRequestStatus;
  }

  /**
   * Get the cadence details for the corresponding cadence ID, undefined if not yet loaded or does not exist.
   * @param cadenceId
   */
  static getCadenceByCadenceId(cadenceId: number): (state: CommonCollectionsStateModel) => Cadence {
    return createSelector([CommonCollectionsState], (state: CommonCollectionsStateModel) =>
      cadenceId && state.cadences.status === LoadableStatus.LOADED
        ? state.cadences.data.find((cadence) => cadence?.id === cadenceId)
        : undefined,
    );
  }

  //#endregion

  //#region Sequences

  /**
   * Get the status of the request for the Sequence update
   * @param state
   */
  @Selector()
  static getSequenceUpdateRequestStatus(state: CommonCollectionsStateModel): LoadableStatus {
    return state.sequenceUpdateRequestStatus;
  }

  @Selector()
  static getSequenceAggregatesLoadables(state: CommonCollectionsStateModel): Loadable<SequenceAggregate[]>[] {
    return state.sequenceAggregates;
  }

  static getSequenceAggregatesLoadableByCadenceId(
    cadenceId: number,
  ): (state: CommonCollectionsStateModel) => Loadable<SequenceAggregate[]> {
    return createSelector([CommonCollectionsState], (state) =>
      state.sequenceAggregates.find((sequenceAggregates) => sequenceAggregates.identity === cadenceId),
    );
  }

  /**
   * Get the sequence aggregates by cadence Id. Currently there will only be one sequence per cadence, but that is expected to change
   * @param cadenceId
   */
  static getSequenceAggregatesByCadenceId(cadenceId: number): (aggregates: Loadable<SequenceAggregate[]>) => SequenceAggregate[] {
    return createSelector([CommonCollectionsState.getSequenceAggregatesLoadableByCadenceId(cadenceId)], (aggregates) => aggregates?.data);
  }

  /**
   * Get the Sequence by sequence Id
   * @param sequenceId
   */
  static getSequenceBySequenceId(sequenceId: number): (state: CommonCollectionsStateModel) => CollectionsSequence {
    return createSelector(
      [CommonCollectionsState],
      (state: CommonCollectionsStateModel) =>
        state.sequences.find(
          (sequence) => sequenceId && sequence.status === LoadableStatus.LOADED && sequence.data?.sequenceId === sequenceId,
        )?.data?.sequence,
    );
  }

  /**
   * Get the status of the sequence by its id
   * @param sequenceId
   */
  static getSequenceStatusBySequenceId(sequenceId: number): (state: CommonCollectionsStateModel) => LoadableStatus {
    return createSelector([CommonCollectionsState], (state: CommonCollectionsStateModel) => {
      const sequence: Loadable<SequenceWrapper> = state.sequences.find(
        (sequence) => sequenceId && sequence.status === LoadableStatus.LOADED && sequence.data?.sequenceId === sequenceId,
      );
      return sequence ? sequence.status : LoadableStatus.DEFAULT;
    });
  }

  //#endregion

  //#region Statements

  @Selector()
  static getStatementsLoadables(state: CommonCollectionsStateModel): Loadable<CollectionsStatements>[] {
    return state.statements;
  }

  static getStatementsLoadable(identity: number): (statements: Loadable<CollectionsStatements>[]) => Loadable<CollectionsStatements> {
    return createSelector([CommonCollectionsState.getStatementsLoadables], (allStatements) =>
      allStatements.find((statements) => statements.identity === identity),
    );
  }

  static getStatementsStatus(identity: number): (statements: Loadable<CollectionsStatements>) => LoadableStatus {
    return createSelector([CommonCollectionsState.getStatementsLoadable(identity)], (statement) => statement?.status);
  }

  static getStatements(identity: number): (statement: Loadable<CollectionsStatements>) => CollectionsStatements {
    return createSelector([CommonCollectionsState.getStatementsLoadable(identity)], (statement) => statement?.data);
  }

  @Selector()
  static getReviewStatementLoadables(state: CommonCollectionsStateModel): Loadable<Statement>[] {
    return state.reviewStatements;
  }

  static getReviewStatementLoadable(
    recipientId: number,
  ): (...params: [boolean, Loadable<Statement>[], Loadable<CollectionsStatements>]) => Loadable<Statement | BasicStatementV2> {
    return createSelector(
      [
        LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2),
        CommonCollectionsState.getReviewStatementLoadables,
        CommonCollectionsState.getStatementsLoadable(recipientId),
      ],
      (isCollectionsV2Enabled, reviewStatements, statements) =>
        isCollectionsV2Enabled
          ? { ...statements, data: (statements?.data ?? []).find((statement) => statement.status === StatementStatus.STAGED) }
          : (reviewStatements ?? []).find(({ identity }) => identity === recipientId),
    );
  }

  static getReviewStatementStatus(recipientId: number): (reviewStatements: Loadable<Statement>) => LoadableStatus {
    return createSelector([CommonCollectionsState.getReviewStatementLoadable(recipientId)], (reviewStatement) => reviewStatement?.status);
  }

  static getReviewStatement(recipientId: number): (reviewStatements: Loadable<Statement>) => Statement {
    return createSelector([CommonCollectionsState.getReviewStatementLoadable(recipientId)], (reviewStatement) => reviewStatement?.data);
  }

  @Selector()
  static getStatementSkipRequests(state: CommonCollectionsStateModel): Loadable<undefined>[] {
    return state.statementSkipRequests;
  }

  @Selector()
  static getStatementApproveRequests(state: CommonCollectionsStateModel): Loadable<undefined>[] {
    return state.statementApproveRequests;
  }

  @Selector()
  static getStatementUndoRequests(state: CommonCollectionsStateModel): Loadable<undefined>[] {
    return state.statementUndoRequests;
  }

  /**
   * Get all statements for the given recipient.
   * Call me a wizard or a fisher cuz I be ___CASTIN'___.
   * @param recipientId
   */
  static getStatementHistory(
    recipientId: number,
  ): (...params: [boolean, CollectionsStatements, Statement, StatementRecipient]) => CollectionsStatements {
    return createSelector(
      [
        LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2),
        CommonCollectionsState.getStatements(recipientId),
        CommonCollectionsState.getReviewStatement(recipientId),
        CommonCollectionsState.getStatementRecipient(recipientId),
      ],
      (isCollectionsV2Enabled, statements, reviewStatement, recipient) => {
        if (isCollectionsV2Enabled) {
          const stagedStatementIndex = ((statements ?? []) as BasicStatementV2[]).findIndex(
            (statement) => statement.status === StatementStatus.STAGED,
          );

          return recipient?.enabled && stagedStatementIndex > -1
            ? moveArrayItem(statements as BasicStatementV2[], stagedStatementIndex, 0)
            : ((statements ?? []) as BasicStatementV2[]).filter(({ status }) => status !== StatementStatus.STAGED);
        }

        // staged and review are synonymous
        const stagedStatement = recipient?.enabled && reviewStatement ? [reviewStatement] : ([] as Statement[]);
        return stagedStatement.concat((statements ?? []) as Statement[]);
      },
    );
  }

  static getStatementHistoryStatus(
    recipientId: number,
  ): (statementsStatus: LoadableStatus, reviewStatementStatus: LoadableStatus) => LoadableStatus {
    return createSelector(
      [CommonCollectionsState.getStatementsStatus(recipientId), CommonCollectionsState.getReviewStatementStatus(recipientId)],
      (statementsStatus, reviewStatementStatus) => LoadableHelper.coalesceStatus([statementsStatus, reviewStatementStatus]),
    );
  }

  //#endregion

  //#region Message Settings

  @Selector()
  static getMessageSettingsUpdateRequestStatus(state: CommonCollectionsStateModel): LoadableStatus {
    return state.messageSettingsUpdateRequestStatus;
  }

  /**
   * Get the status of the request for the message settings
   * @param state
   */
  @Selector()
  static getMessageSettingsStatus(state: CommonCollectionsStateModel): LoadableStatus {
    return state.messageSettings.status;
  }

  /**
   * Get the latest stored message settings
   * @param state
   */
  @Selector()
  static getMessageSettings(state: CommonCollectionsStateModel): SmartStatementActionDefinitionJson {
    return state.messageSettings?.data?.action_def_json;
  }

  /**
   * Get variables for the message settings
   * @param state
   */
  @Selector()
  static getMessageVariables(state: CommonCollectionsStateModel): Map<string, string> {
    return state.messageVariables.data ? this._getMessageVariablesMap(state.messageVariables.data) : new Map<string, string>();
  }

  private static _getMessageVariablesMap(data: DynamicField[]): Map<string, string> {
    return data.reduce((variableMap, variable) => variableMap.set(variable.display_name, variable.mapped_field), new Map<string, string>());
  }

  /**
   * Get status for variables request
   * @param state
   */
  @Selector()
  static getMessageVariablesStatus(state: CommonCollectionsStateModel): LoadableStatus {
    return state.messageVariables.status;
  }

  //#endregion

  //#region Aggregates

  @Selector()
  static getAllRecipientAggregatesLoadables(state: CommonCollectionsStateModel): Loadable<RecipientOutstandingAggregates>[] {
    return state.aggregates;
  }

  static getRecipientAggregatesLoadable(
    recipientId: number,
  ): (aggregates: Loadable<RecipientOutstandingAggregates>[]) => Loadable<RecipientOutstandingAggregates> {
    return createSelector([CommonCollectionsState.getAllRecipientAggregatesLoadables], (aggregates) =>
      aggregates.find(({ identity }) => identity === recipientId),
    );
  }

  /**
   * Get the aggregated past due data for the given client Id
   * @param recipientId
   */
  static getRecipientAggregates(
    recipientId: number,
  ): (aggregates: Loadable<RecipientOutstandingAggregates>) => RecipientOutstandingAggregates {
    return createSelector([CommonCollectionsState.getRecipientAggregatesLoadable(recipientId)], (aggregates) => aggregates?.data);
  }

  /**
   * Get the status of the aggregate request for the given client Id
   * @param recipientId
   */
  static getRecipientAggregatesStatus(recipientId: number): (aggregates: Loadable<RecipientOutstandingAggregates>) => LoadableStatus {
    return createSelector(
      [CommonCollectionsState.getRecipientAggregatesLoadable(recipientId)],
      (aggregates) => aggregates?.status ?? LoadableStatus.DEFAULT,
    );
  }

  //#endregion

  //#region Recipient Exclusions

  /**
   * Get number of excluded clients from state.
   * @param state
   */
  @Selector()
  static getAlreadyExcludedCollectionsClientsTotal(state: CommonCollectionsStateModel): number {
    return state.alreadyExcludedClientsTotal.data || 0;
  }

  /**
   * Selector to get current list of excluded clients for all pages that have been requests
   * @param state
   */
  @Selector()
  static getExcludedCollectionsClients(state: CommonCollectionsStateModel): ClientGQLDisplay[] | CommunicationRecipientRow[] {
    return state.excludedClients.pages.reduce(
      (accumulatedClients, currentClientPage) => [...currentClientPage.data.data, ...accumulatedClients],
      [],
    );
  }

  /**
   * Get the load status of the latest request for excluded clients.
   * @param state
   */
  @Selector()
  static getLatestExcludedCollectionsClientsPageStatus(state: CommonCollectionsStateModel): LoadableStatus {
    return (
      state.excludedClients.pages.reduce(
        (accumulator, current) => (!accumulator || current.data.pageNumber > accumulator.data.pageNumber ? current : accumulator),
        undefined,
      )?.status || LoadableStatus.LOADING
    );
  }

  /**
   * Get the load status of the latest request for excluded clients.
   * @param state
   */
  @Selector()
  static getLatestExcludedCollectionsClientsPageNumber(state: CommonCollectionsStateModel): number {
    return (state.excludedClients.pages ?? []).reverse()[0]?.data.pageNumber ?? 1;
  }

  /**
   * Get column and direction of latest sort used in request for excluded clients.
   * @param state
   */
  @Selector()
  static getLatestExcludedClientsSort(state: CommonCollectionsStateModel): Sort {
    return state.latestExcludedClientsSort;
  }

  /**
   * Get the total number of excluded client pages.
   * @param state
   */
  @Selector()
  static getTotalExcludedCollectionsClientPages(state: CommonCollectionsStateModel): number {
    return state.excludedClients.totalPages;
  }

  /**
   * For the given search term, what is the correlating search's status.
   * @param searchTerm
   */
  static searchExcludedCollectionsClientsStatus(searchTerm: string): (state: CommonCollectionsStateModel) => LoadableStatus {
    return createSelector([CommonCollectionsState], (state: CommonCollectionsStateModel) => {
      const search = state.excludedClientSearchResults.find((search) => search.term === searchTerm.toLowerCase());
      return search ? search.results.status : LoadableStatus.DEFAULT;
    });
  }

  /**
   * Get the excluded client results from the search in state matching the term.
   * @param searchTerm
   */
  static searchExcludedCollectionsClients(
    searchTerm: string,
  ): (state: CommonCollectionsStateModel) => ClientGQLDisplay[] | CommunicationRecipientRow[] {
    return createSelector(
      [CommonCollectionsState],
      (state: CommonCollectionsStateModel) =>
        state.excludedClientSearchResults.find((search) => search.term === searchTerm.toLowerCase())?.results.data || [],
    );
  }

  /**
   * Get the excluded client results from the search matching the term
   * @param state
   */
  @Selector()
  static getExclusionStatusUpdates(state: CommonCollectionsStateModel): Loadable<ExclusionStatusUpdate[]> {
    return state.exclusionStatusUpdates;
  }

  //#endregion

  //#region Staff

  @Selector()
  static getStaffLoadable(state: CommonCollectionsStateModel): Loadable<StaffGQL[]> {
    return state.staff;
  }

  @Selector([CommonCollectionsState.getStaffLoadable])
  static getStaffStatus(staff: Loadable<StaffGQL[]>): LoadableStatus {
    return staff.status;
  }

  @Selector([CommonCollectionsState.getStaffLoadable])
  static getStaff(staff: Loadable<StaffGQL[]>): StaffGQL[] {
    return staff.data;
  }

  //#endregion

  //#region Settings

  //#region v1

  @Selector()
  static getSmartStatementBucketSettings(state: CommonCollectionsStateModel): SmartStatementBucketSetting[] {
    return state.smartStatementBucketSettings?.data;
  }

  @Selector()
  static getSmartStatementBucketSettingsStatus(state: CommonCollectionsStateModel): LoadableStatus {
    return state.smartStatementBucketSettings?.status;
  }

  @Selector([CommonCollectionsState.getSmartStatementBucketSettings])
  static getSmartStatementZeroToThirtyBucketSettings(settings: SmartStatementBucketSetting[]): SmartStatementBucketSetting {
    return settings.find((setting) => setting.smartStatementBucket === SmartStatementBucket.ZERO_TO_THIRTY);
  }

  //#endregion

  //#region v2

  @Selector()
  static getSettings(state: CommonCollectionsStateModel): CollectionsSettingsStateModel {
    return state.settings;
  }

  /**
   * One-stop shop for everything collections settings has to offer.
   * @param path
   */
  static getCollectionsSetting<Path extends ObjectTraversalPath<CollectionsSettingsStateModel>>(
    ...path: Path
  ): (settings: CollectionsSettingsStateModel) => ObjectTraversalResult<CollectionsSettingsStateModel, Path> {
    return createSelector([CommonCollectionsState.getSettings], (collectionsSettingsState: CollectionsSettingsStateModel) =>
      getNestedValue(collectionsSettingsState, ...path),
    );
  }

  //#endregion

  //#endregion

  //#endregion

  constructor(
    private collectionsApiService: CollectionsApiService,
    private readonly authService: AuthService,
    private readonly store: Store,
    private readonly logger: Logger,
  ) {}

  //#region Actions (and action helpers)

  //#region Recipients

  //#region v1

  /**
   * Request the next "page" of clients to add to the client list collection.
   * @param context state
   * @param page the next page to request
   */
  @Action([GetCollectionsRecipients])
  getCollectionsClients(
    context: StateContext<CommonCollectionsStateModel>,
    { page, partner, sortOption }: GetCollectionsRecipients,
  ): Observable<StatementRecipientModel[] | LoadableStatus> {
    // if the page has not already been requested, or the request failed, make the request for the given page
    let pagedData: PagedData<number> = PagedData.deserialize(context.getState().pagedRecipientResults);
    const extraParameters = context.getState().pagedExtraParameters;

    const isPageAlreadyLoaded = !LoadableHelper.shouldRequest(pagedData.getPageStatus(page));
    const areExtraFiltersEqualToThePreviousOnes = this.areExtraParameterEqual({ partner, sortOption }, extraParameters);

    if (isPageAlreadyLoaded && areExtraFiltersEqualToThePreviousOnes) {
      return this.store.select(CommonCollectionsState.getLatestRecipientPageStatus).pipe(isLoaded(true), take(1));
    }

    // Case that the extra filters changed, so we will need to reset the pages
    if (isPageAlreadyLoaded && !areExtraFiltersEqualToThePreviousOnes) {
      pagedData = PagedData.deserialize(DEFAULT_PAGE_RECIPIENT_RESULTS);
    }

    // Set the page status as loading
    pagedData.updatePage(page, LoadableStatus.LOADING, []);
    context.patchState({
      pagedRecipientResults: pagedData.serialize(),
      pagedExtraParameters: { partner, sortOption },
    });

    return this.store.select(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2)).pipe(
      switchMap((isEnabled) => {
        if (isEnabled) {
          return this.collectionsApiService.getStatementRecipients({
            mode: this.store.selectSnapshot(CommonCollectionsState.getCollectionsMode),
            ...pagedData.getPaginationVariables(page),
            partnerName: partner,
            sortOption,
          });
        }

        return this.collectionsApiService.getClientCadenceList(pagedData.getPaginationVariables(page), partner, sortOption);
      }),
      tap({
        next: (response: StatementRecipientModel[]) => {
          const existingRecipients = this.store.selectSnapshot(CommonCollectionsState.getRawStatementRecipients);
          const newRecipientIds = CommonCollectionsState.mapRecipientsToIds(response);

          context.patchState({
            recipients: CommonCollectionsState.coalesceRecipients(existingRecipients, response),
            pagedRecipientResults: PagedData.deserialize(context.getState().pagedRecipientResults)
              .updatePage(page, LoadableStatus.LOADED, newRecipientIds)
              .serialize(),
          });
        },
        error: (_) =>
          context.patchState({
            pagedRecipientResults: PagedData.deserialize(context.getState().pagedRecipientResults)
              .updatePage(page, LoadableStatus.ERROR, [])
              .serialize(),
          }),
      }),
    );
  }

  private areExtraParameterEqual(
    ep1: CommonCollectionsStateModelPagedExtraParameters,
    ep2: CommonCollectionsStateModelPagedExtraParameters,
  ): boolean {
    // Object has same reference, then equal
    if (ep1 === ep2) {
      return true;
    }

    if (!ep1 || !ep2) {
      return false;
    }

    const areSortEqual = ep1.sortOption?.direction === ep2.sortOption?.direction && ep1.sortOption?.field === ep2.sortOption?.field;
    const arePartnersEqual = ep1.partner === ep2.partner;

    return arePartnersEqual && areSortEqual;
  }

  @Action([SearchCollectionsRecipients])
  searchCollectionsRecipients(
    context: StateContext<CommonCollectionsStateModel>,
    { term, partnerName, sort }: SearchCollectionsRecipients,
  ): Observable<number[]> | void {
    return this.handleSearch(
      context,
      CommonCollectionsState.multiFieldToCacheKey(term, partnerName, CommonCollectionsState.sortToStr(sort)),
      this.store.selectSnapshot(CommonCollectionsState.getRawRecipientSearchResults),
      (cacheKey) => {
        const [searchTerm] = CommonCollectionsState.cacheKeyToMultiFieldValue(cacheKey);

        const apiCall$: Observable<StatementRecipientModel[]> = this.store.selectSnapshot(
          LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2),
        )
          ? this.collectionsApiService.getStatementRecipients({
              mode: this.store.selectSnapshot(CommonCollectionsState.getCollectionsMode),
              searchTerm,
              partnerName,
              sortOption: sort,
            })
          : this.collectionsApiService.searchClientCadenceList(searchTerm, partnerName, sort);

        return apiCall$.pipe(
          tap((recipients) =>
            context.patchState({
              recipients: CommonCollectionsState.coalesceRecipients(context.getState().recipients, recipients),
            }),
          ),
          map((recipients) => CommonCollectionsState.mapRecipientsToIds(recipients)),
        );
      },
      'recipientSearchResults',
    );
  }

  @Action([Collections_UpdateRecipientExclusion])
  updateCollectionsExclusion(
    _: StateContext<CommonCollectionsStateModel>,
    { clientId, excluded }: UpdateClientExclusion,
  ): Observable<unknown> {
    const targetRecipient = this.store.selectSnapshot(CommonCollectionsState.getRawStatementRecipient(clientId));

    return this.collectionsApiService.updateClientCollectionsExclusion(clientId, excluded).pipe(
      filter((updatedRowCount) => updatedRowCount > 0 && !!targetRecipient),
      switchMap((_) => this.store.dispatch(new Clients_UpdateClientExclusion(clientId, excluded))),
    );
  }

  //#endregion

  /**
   * Get the past due aggregate summaries for the given client id
   * @param context
   * @param clientId
   */
  @Action([GetOutstandingSummaryByRecipient])
  getOutstandingSummaryByRecipient(
    context: StateContext<CommonCollectionsStateModel>,
    { clientId }: GetOutstandingSummaryByRecipient,
  ): Observable<RecipientOutstandingAggregates> | void {
    const targetAggregate = context.getState().aggregates.find(({ identity }) => identity === clientId);
    if (!LoadableHelper.shouldRequest(targetAggregate?.status)) {
      return;
    }

    context.patchState({
      aggregates: this.updateLoadableArrayItem(context.getState().aggregates, clientId, LoadableStatus.LOADING),
    });

    const apiCall$ = this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2))
      ? this.collectionsApiService.getRecipientAggregates(clientId)
      : this.collectionsApiService.getClientCadenceAggregates(clientId);

    return apiCall$.pipe(
      tap({
        next: (response) =>
          context.patchState({
            aggregates: this.updateLoadableArrayItem(context.getState().aggregates, clientId, LoadableStatus.LOADED, response),
          }),
        error: (_) =>
          context.patchState({
            aggregates: this.updateLoadableArrayItem(context.getState().aggregates, clientId, LoadableStatus.ERROR),
          }),
      }),
    );
  }

  /**
   * When statement is approved, skipped, or unskipped, update the status and lastStatementDate of the recipient locally instead of
   * refreshing it from the API.
   */
  @Action([UpdateRecipientListStatus])
  updateRecipientListStatus(context: StateContext<CommonCollectionsStateModel>, { recipientId, status }: UpdateRecipientListStatus): void {
    const targetRecipient = this.store.selectSnapshot(CommonCollectionsState.getRawStatementRecipient(recipientId));
    if (!targetRecipient) {
      return;
    }

    const existingRecipients = this.store.selectSnapshot(CommonCollectionsState.getRawStatementRecipients);
    const now = moment().format();
    const updatedRecipient: StatementRecipientModel = {
      ...targetRecipient,
      status,
      ...(CommonCollectionsState.isV1Recipient(targetRecipient) ? { last_statement_date: now } : { lastStatementDate: now }),
    };

    context.patchState({ recipients: CommonCollectionsState.coalesceRecipients(existingRecipients, [updatedRecipient]) });
  }

  @Action([SetSelectedStatementRecipientId])
  setSelectedStatementRecipientId(
    { patchState }: StateContext<CommonCollectionsStateModel>,
    { recipientId }: SetSelectedStatementRecipientId,
  ): void {
    patchState({ selectedStatementRecipientId: recipientId });
  }

  @Action([ClearCollectionsPageData])
  clearCollectionsFeatureData({ patchState }: StateContext<CommonCollectionsStateModel>): void {
    patchState({
      /** Feature > Sidebar */
      recipients: [],
      pagedRecipientResults: {
        limit: DEFAULT_RECIPIENT_PAGE_LIMIT,
        pages: [],
      },
      recipientSearchResults: [],
      selectedStatementRecipientId: undefined,

      /** Feature > Content */
      aggregates: [],
      statements: [],
      reviewStatements: [],
      statementSkipRequests: [],
      statementApproveRequests: [],
      statementUndoRequests: [],
    });
  }

  //#endregion

  //#region Client Cadences and Sequences

  @Action([GetActiveCadences])
  getActiveCadences(context: StateContext<CommonCollectionsStateModel>): Observable<unknown> | void {
    if (LoadableHelper.shouldRequest(context.getState().cadences.status)) {
      return this.handleLoadableRequest(context, 'cadences', this.collectionsApiService.getActiveCadences());
    }
  }

  @Action([GetSequenceAggregatesByCadenceId])
  getSequenceAggregatesByCadenceId(
    context: StateContext<CommonCollectionsStateModel>,
    { cadenceId }: GetSequenceAggregatesByCadenceId,
  ): Observable<unknown> | void {
    const existingAggregate = this.store.selectSnapshot(CommonCollectionsState.getSequenceAggregatesLoadableByCadenceId(cadenceId));
    if (!LoadableHelper.shouldRequest(existingAggregate?.status)) {
      return;
    }

    context.patchState({
      sequenceAggregates: [
        ...context.getState().sequenceAggregates.filter((aggregate) => aggregate.identity !== cadenceId),
        { identity: cadenceId, status: LoadableStatus.LOADING },
      ],
    });

    return this.collectionsApiService.getSequenceAggregatesByCadenceId(cadenceId).pipe(
      tap({
        next: (sequenceAggregate) =>
          context.patchState({
            sequenceAggregates: [
              ...context.getState().sequenceAggregates.filter((aggregate) => aggregate.identity !== cadenceId),
              {
                identity: cadenceId,
                status: LoadableStatus.LOADED,
                data: sequenceAggregate,
              },
            ],
          }),
        error: (error) =>
          context.patchState({
            sequenceAggregates: [
              ...context.getState().sequenceAggregates.filter((aggregate) => aggregate.identity !== cadenceId),
              {
                identity: cadenceId,
                status: LoadableStatus.ERROR,
                error,
              },
            ],
          }),
      }),
      // Map the sequences to requests for triggers
      map((response) =>
        response
          .map((sequenceAggregate) => sequenceAggregate.sequence_id)
          .filter(Boolean)
          .map((sequenceId) => new GetSequenceBySequenceId(sequenceId)),
      ),
      // Get the Triggers that are associated with the sequences
      switchMap((getTriggersBySequenceIdActions) => context.dispatch([...getTriggersBySequenceIdActions])),
    );
  }

  @Action([GetSequenceBySequenceId])
  getSequenceBySequenceId(
    context: StateContext<CommonCollectionsStateModel>,
    { sequenceId }: GetSequenceBySequenceId,
  ): Observable<CollectionsSequence | SequenceWrapper[]> {
    const existingSequences = context.getState().sequences;
    const loadingState = getLoadableArrayLoadingState(
      existingSequences,
      LoadableArrayLoadStrategy.LOAD,
      (sequence) => sequence.sequenceId,
      [sequenceId],
    );

    if (loadingState == undefined) {
      this.logger.debug('CommonCollectionsState : GetSequenceBySequenceId : Unnecessary request. Skipping.');
      return this.store.select(CommonCollectionsState.getSequenceBySequenceId(sequenceId)).pipe(ignoreNullish(), take(1));
    }

    context.patchState({ sequences: loadingState.loadableArray });

    return this.collectionsApiService.getSequenceBySequenceId(sequenceId).pipe(
      map((sequence) => [{ sequenceId, sequence }]),
      setLoadableArrayInState(context.patchState, 'sequences', existingSequences, (sequence) => sequence.sequenceId),
    );
  }

  @Action([UpdateCadence])
  updateCadence(context: StateContext<CommonCollectionsStateModel>, { cadence }: UpdateCadence): Observable<unknown> | void {
    if (cadence && context.getState().cadenceUpdateRequestStatus !== LoadableStatus.LOADING) {
      context.patchState({ cadenceUpdateRequestStatus: LoadableStatus.LOADING });

      return this.collectionsApiService.updateCadence(MarshallUpdateCadenceRequest(cadence)).pipe(
        tap({
          next: () =>
            context.patchState({
              cadenceUpdateRequestStatus: LoadableStatus.LOADED,
              cadences: {
                ...context.getState().cadences,
                data: [...context.getState().cadences.data.filter((cachedCadence) => cachedCadence.id !== cadence.id), cadence],
              },
            }),
          error: (error) =>
            context.patchState({
              cadenceUpdateRequestStatus: LoadableStatus.ERROR,
            }),
        }),
      );
    }
  }

  @Action([UpdateSequence])
  updateSequence(context: StateContext<CommonCollectionsStateModel>, { sequence }: UpdateSequence): Observable<unknown> | void {
    if (sequence && context.getState().sequenceUpdateRequestStatus !== LoadableStatus.LOADING) {
      context.patchState({ sequenceUpdateRequestStatus: LoadableStatus.LOADING });

      return this.collectionsApiService.updateSequence(MarshallUpdateSequenceRequest(sequence)).pipe(
        tap({
          next: () =>
            context.patchState({
              sequenceUpdateRequestStatus: LoadableStatus.LOADED,
              sequences: [
                ...context.getState().sequences.filter((sequenceTrigger) => sequenceTrigger.data?.sequenceId !== sequence.id),
                {
                  status: LoadableStatus.LOADED,
                  data: {
                    sequenceId: sequence.id,
                    sequence,
                  },
                },
              ],
            }),
          error: (_) =>
            context.patchState({
              sequenceUpdateRequestStatus: LoadableStatus.ERROR,
            }),
        }),
      );
    }
  }

  //#endregion

  //#region Statements

  @Action([GetRecipientPreviousStatements])
  getRecipientPreviousStatements(
    context: StateContext<CommonCollectionsStateModel>,
    { recipientId, forceRefresh }: GetRecipientPreviousStatements,
  ): Observable<CollectionsStatements> {
    const existingStatements = this.store.selectSnapshot(CommonCollectionsState.getStatementsLoadable(recipientId));

    if (!LoadableHelper.shouldRequest(existingStatements?.status) && !forceRefresh) {
      return getLoadedResults(this.store.select(CommonCollectionsState.getStatementsLoadable(recipientId)), true).pipe(take(1));
    }

    context.patchState({
      statements: [
        ...context.getState().statements.filter(({ identity }) => identity !== recipientId),
        { identity: recipientId, status: LoadableStatus.LOADING },
      ],
    });

    const apiCall$ = defer(() =>
      this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2))
        ? this.collectionsApiService.getRecipientStatements(recipientId)
        : this.collectionsApiService.getClientCadenceStatements(recipientId),
    );

    return apiCall$.pipe(
      tap({
        next: (data) =>
          context.patchState({
            statements: [
              ...context.getState().statements.filter(({ identity }) => identity !== recipientId),
              { identity: recipientId, status: LoadableStatus.LOADED, data },
            ],
          }),
        error: (_) =>
          context.patchState({
            statements: [
              ...context.getState().statements.filter(({ identity }) => identity !== recipientId),
              { identity: recipientId, status: LoadableStatus.ERROR },
            ],
          }),
      }),
    );
  }

  @Action([GetClientNextStatement])
  getClientNextStatement(context: StateContext<CommonCollectionsStateModel>, { clientId }: GetClientNextStatement): Observable<unknown> {
    // In v2, this is retrieved altogether in `GetRecipientPreviousStatements`, so this field isn't used.
    if (this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2))) {
      context.patchState({
        reviewStatements: [
          ...context.getState().reviewStatements.filter((statement) => statement.identity !== clientId),
          { identity: clientId, status: LoadableStatus.LOADED },
        ],
      });
      return;
    }

    context.patchState({
      reviewStatements: [
        ...context.getState().reviewStatements.filter((statement) => statement.identity !== clientId),
        { identity: clientId, status: LoadableStatus.LOADING },
      ],
    });

    // No cache implemented, needs to refresh every time it is pulled
    return this.collectionsApiService.getClientStagedStatement(clientId).pipe(
      tap({
        next: (response) =>
          context.patchState({
            reviewStatements: [
              ...context.getState().reviewStatements.filter((statement) => statement.identity !== clientId),
              {
                identity: clientId,
                status: LoadableStatus.LOADED,
                data: response?.[0],
              },
            ],
          }),
        error: (error) =>
          context.patchState({
            reviewStatements: [
              ...context.getState().reviewStatements.filter((statement) => statement.identity !== clientId),
              { identity: clientId, status: LoadableStatus.ERROR, error },
            ],
          }),
      }),
    );
  }

  @Action([Collections_SkipStatement])
  skipStatement(
    { patchState, getState, dispatch }: StateContext<CommonCollectionsStateModel>,
    { recipientId, actionId }: Collections_SkipStatement,
  ): Observable<unknown> | void {
    const targetRequest: Loadable<undefined> = getState().statementSkipRequests.find((request) => request.identity === recipientId);
    const stagedAction = this.store.selectSnapshot(CommonCollectionsState.getReviewStatementLoadable(recipientId))?.data;

    if (stagedAction?.id === actionId && LoadableHelper.shouldRequest(targetRequest?.status)) {
      // Mark the request as underway
      patchState({
        statementSkipRequests: [
          ...getState().statementSkipRequests.filter((request) => request.identity !== recipientId),
          { identity: recipientId, status: LoadableStatus.LOADING },
        ],
      });

      const apiCallKey: keyof CollectionsApiService & ('skipStatementAction' | 'skipStatement') = this.store.selectSnapshot(
        LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2),
      )
        ? 'skipStatement'
        : 'skipStatementAction';

      return this.collectionsApiService[apiCallKey](actionId).pipe(
        tap({
          next: (_) =>
            patchState({
              statementSkipRequests: [
                ...getState().statementSkipRequests.filter((request) => request.identity !== recipientId),
                { identity: recipientId, status: LoadableStatus.LOADED },
              ],
              statementUndoRequests: getState().statementUndoRequests.filter((request) => request.identity !== recipientId),
              reviewStatements: [...getState().reviewStatements.filter((statement) => statement.identity !== recipientId)],
            }),
          error: (_) =>
            patchState({
              statementSkipRequests: [
                ...getState().statementSkipRequests.filter((request) => request.identity !== recipientId),
                { identity: recipientId, status: LoadableStatus.ERROR },
              ],
            }),
        }),
        // Get the updated history, there are some fields that can only be pulled from the server
        switchMap((_) => dispatch(new SkipStatementComplete(recipientId))),
        take(1),
      );
    }
  }

  @Action([SkipStatementComplete])
  skipStatementComplete({ dispatch }: StateContext<CommonCollectionsStateModel>, { recipientId }: SkipStatementComplete): void {
    dispatch([new UpdateRecipientListStatus(recipientId, StatementStatus.DECLINED), new GetRecipientPreviousStatements(recipientId, true)]);
  }

  /**
   * Undo the previously skipped last statement, and then pull the fresh history
   */
  @Action([Collections_UndoSkipStatement])
  undoSkipStatement(
    { patchState, getState, dispatch }: StateContext<CommonCollectionsStateModel>,
    { actionId, recipientId }: UndoSkipStatement,
  ): Observable<unknown> {
    const targetRequest: Loadable<undefined> = getState().statementUndoRequests.find(({ identity }) => identity === recipientId);

    if (!LoadableHelper.shouldRequest(targetRequest?.status)) {
      return this.store.select(CommonCollectionsState.getStatementUndoRequests).pipe(
        map((requests) => requests.find(({ identity }) => identity === recipientId)?.status),
        isLoaded(true),
        take(1),
      );
    }

    patchState({
      statementUndoRequests: [
        ...getState().statementUndoRequests.filter(({ identity }) => identity !== recipientId),
        { identity: recipientId, status: LoadableStatus.LOADING },
      ],
    });

    const apiCallKey: keyof CollectionsApiService & ('undoSkipStatementV2' | 'undoSkipStatement') = this.store.selectSnapshot(
      LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2),
    )
      ? 'undoSkipStatementV2'
      : 'undoSkipStatement';

    return this.collectionsApiService[apiCallKey](actionId).pipe(
      tap({
        next: (_) =>
          patchState({
            statementUndoRequests: [
              ...getState().statementUndoRequests.filter(({ identity }) => identity !== recipientId),
              { identity: recipientId, status: LoadableStatus.LOADED },
            ],
            statementSkipRequests: getState().statementSkipRequests.filter(({ identity }) => identity !== recipientId),
          }),
        error: (_) =>
          patchState({
            statementUndoRequests: [
              ...getState().statementUndoRequests.filter(({ identity }) => identity !== recipientId),
              { identity: recipientId, status: LoadableStatus.ERROR },
            ],
          }),
      }),
      switchMap((_) => dispatch(new UndoSkipStatementComplete(recipientId))),
    );
  }

  @Action([UndoSkipStatementComplete])
  undoSkipStatementComplete({ dispatch }: StateContext<CommonCollectionsStateModel>, { recipientId }: UndoSkipStatementComplete): void {
    dispatch([
      new UpdateRecipientListStatus(recipientId, StatementStatus.STAGED),
      new GetRecipientPreviousStatements(recipientId, true),
      new GetClientNextStatement(recipientId),
    ]);
  }

  @Action([Collections_ApproveStatement])
  approveStatement(
    { patchState, getState, dispatch }: StateContext<CommonCollectionsStateModel>,
    { recipientId, action }: Collections_ApproveStatement,
  ): Observable<unknown> | void {
    const targetRequest: Loadable<undefined> = getState().statementApproveRequests.find((request) => request.identity === recipientId);
    const stagedAction = this.store.selectSnapshot(CommonCollectionsState.getReviewStatementLoadable(recipientId))?.data;

    if (stagedAction?.id === ('id' in action ? action.id : action.communicationId) && LoadableHelper.shouldRequest(targetRequest?.status)) {
      // Mark the request as underway
      patchState({
        statementApproveRequests: [
          ...getState().statementApproveRequests.filter((request) => request.identity !== recipientId),
          { identity: recipientId, status: LoadableStatus.LOADING },
        ],
      });

      return this.store.selectOnce(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2)).pipe(
        switchMap((enabled) =>
          enabled
            ? this.collectionsApiService.approveStatement(action as SendStatementPayload)
            : this.collectionsApiService.approveStatementAction(action as NextStatement),
        ),
        tap({
          next: (_) =>
            patchState({
              statementApproveRequests: [
                ...getState().statementApproveRequests.filter((request) => request.identity !== recipientId),
                { identity: recipientId, status: LoadableStatus.LOADED },
              ],
            }),
          error: (_) =>
            patchState({
              statementApproveRequests: [
                ...getState().statementApproveRequests.filter((request) => request.identity !== recipientId),
                { identity: recipientId, status: LoadableStatus.ERROR },
              ],
            }),
        }),
        switchMap((_) => dispatch(new ApproveStatementComplete(recipientId))),
        take(1),
      );
    }
  }

  @Action([ApproveStatementComplete])
  approveStatementComplete(
    { patchState, dispatch }: StateContext<CommonCollectionsStateModel>,
    { recipientId }: ApproveStatementComplete,
  ): void {
    const existingReviewStatements = this.store.selectSnapshot(CommonCollectionsState.getReviewStatementLoadables);
    patchState({ reviewStatements: existingReviewStatements.filter(({ identity }) => identity !== recipientId) });

    dispatch([new UpdateRecipientListStatus(recipientId, StatementStatus.EXECUTED), new GetRecipientPreviousStatements(recipientId, true)]);
  }

  //#endregion

  //#region Excluded Recipients

  //#region v1

  /**
   * Update client exclusion status(es) and update state based on response/errors.
   * @param context
   * @param clientsToUpdate
   */
  @Action([UpdateCollectionsClientExclusionStatus])
  updateCollectionsClientExclusionStatus(
    context: StateContext<CommonCollectionsStateModel>,
    { clientsToUpdate }: UpdateCollectionsClientExclusionStatus,
  ): Observable<ExclusionStatusUpdate[]> | void {
    // terminate if provided an empty array
    if (!clientsToUpdate?.length) {
      return;
    }

    // separate exclusions and inclusions
    const clientsToExclude = clientsToUpdate
      .filter((clientToUpdate) => clientToUpdate.excludeFromCollections)
      .map((clientToUpdate) => clientToUpdate.clientRef);
    const clientsToInclude = clientsToUpdate
      .filter((clientToUpdate) => !clientToUpdate.excludeFromCollections)
      .map((clientToUpdate) => clientToUpdate.clientRef);

    // avoid making a pointless request
    const excludeClients$ = clientsToExclude.length
      ? this.collectionsApiService.updateCollectionsClientExclusionStatus(clientsToExclude, true, this.authService.getStaffId())
      : of({ affected_rows: 0 });
    const includeClients$ = clientsToInclude.length
      ? this.collectionsApiService.updateCollectionsClientExclusionStatus(clientsToInclude, false, this.authService.getStaffId())
      : of({ affected_rows: 0 });

    // make request(s)
    return combineLatest([excludeClients$, includeClients$]).pipe(
      // interpret responses (error response does not include 'affected_rows')
      map((results) => {
        return results.map((result, index) => {
          if (result.affected_rows === undefined) {
            return {
              status: LoadableStatus.ERROR,
              exclusion: !index,
              affected_rows: 0,
            };
          }
          return {
            status: LoadableStatus.LOADED,
            exclusion: !index,
            affected_rows: result.affected_rows,
          };
        });
      }),
      tap({
        next: (updates: ExclusionStatusUpdate[]) => {
          context.patchState({
            exclusionStatusUpdates: {
              status: updates.some(({ status }) => status === LoadableStatus.ERROR) ? LoadableStatus.ERROR : LoadableStatus.LOADED,
              data: updates,
            },
            recipients: this.store
              .selectSnapshot(CommonCollectionsState.getRawStatementRecipients)
              .map((recipient: GraphQl.ClientCadenceType) => {
                const updatedClient: ClientGQL = clientsToUpdate.find(({ clientRef }) => clientRef === recipient.client_ref);
                if (!updatedClient) {
                  return recipient;
                }

                return { ...recipient, exclude_from_collections: updatedClient.excludeFromCollections };
              }),
          });
        },
        // (this error status is separate from the ones directly above, caused by failed request or runtime error)
        error: (error) =>
          context.patchState({
            exclusionStatusUpdates: {
              status: LoadableStatus.ERROR,
              data: [],
              error,
            },
          }),
      }),
      // get new number of excluded clients in tenant's DB table
      tap(() => context.dispatch(new GetAlreadyExcludedCollectionsClientsTotal())),
    );
  }

  /**
   * Reset properties related to excluded clients that should not persist after submission.
   * @param context
   */
  @Action([ClearExcludedClientsState])
  clearExcludedClientsState(context: StateContext<CommonCollectionsStateModel>): void {
    context.patchState({
      excludedClients: {
        limit: DEFAULT_RECIPIENT_PAGE_LIMIT,
        pages: [],
      },
      excludedClientSearchResults: [],
      latestExcludedClientsSort: undefined,
      exclusionStatusUpdates: undefined,
    });
  }

  /**
   * Get number of excluded clients in the tenant's DB table.
   * @param context
   */
  @Action([GetAlreadyExcludedCollectionsClientsTotal])
  getAlreadyExcludedCollectionsClientsTotal(context: StateContext<CommonCollectionsStateModel>): Observable<number> {
    context.patchState({
      alreadyExcludedClientsTotal: {
        status: LoadableStatus.LOADING,
        // avoid defaulting to 0 while loading after submitting exclusion status updates
        data: context.getState().alreadyExcludedClientsTotal.data || null,
      },
    });

    return this.collectionsApiService
      .getAlreadyExcludedCollectionsClientsTotal()
      .pipe(setLoadableInState(context.patchState, 'alreadyExcludedClientsTotal'));
  }

  /**
   * Purge existing excluded clients paged data and trigger new sorted reqyest for excluded clients.
   * @param context
   * @param sort column and direction of sort, if any
   */
  @Action([SortExcludedStatementRecipients])
  sortExcludedCollectionsClients(
    context: StateContext<CommonCollectionsStateModel>,
    { sort }: SortExcludedStatementRecipients,
  ): Observable<ClientGQLDisplay[] | void> {
    const limit = context.getState().excludedClients.limit;
    context.patchState({
      excludedClients: {
        limit,
        pages: [],
      },
    });
    return context.dispatch(new GetExcludedStatementRecipients(1, sort));
  }

  //#endregion

  //#region Bridge v1 and v2

  /**
   * Request the next "page" of excluded clients to add to the excluded client list collection.
   * @param context state
   * @param page the next page to request
   * @param sort column and direction of sort, if any
   */
  @Action([GetExcludedStatementRecipients])
  getExcludedCollectionsClients(
    context: StateContext<CommonCollectionsStateModel>,
    { page, sort }: GetExcludedStatementRecipients,
  ): Observable<ClientGQLDisplay[] | CommunicationRecipientRow[] | LoadableStatus> | void {
    const pagedData: PagedData<ClientGQLDisplay | CommunicationRecipientRow> = PagedData.deserialize(context.getState().excludedClients);
    const latestSort = context.getState().latestExcludedClientsSort;

    // If the page has already been requested and the sort is the same, return the latest page status
    if (pagedData.hasPage(page) && LoadableHelper.shouldRequest(pagedData.getPageStatus(page)) && latestSort && sort === latestSort) {
      this.logger.debug(
        'CommonCollectionsState : getExcludedCollectionsClients : Unnecessary request. Skipping.',
        `\n\tPage status: ${pagedData.getPageStatus(page)}\n\tSort: ${sort}`,
      );

      return this.store.select(CommonCollectionsState.getClientPagedResults).pipe(
        map((pageStuff) => pageStuff.pages[page].status),
        isLoaded(true),
        take(1),
      );
    }

    // Set the page status as loading
    pagedData.updatePage(page, LoadableStatus.LOADING, []);
    context.patchState({ excludedClients: pagedData.serialize(), latestExcludedClientsSort: sort });

    const getExcludedArguments = [pagedData.getPaginationVariables(page), sort] as [GqlPaginationVariables, Sort];
    return defer(() =>
      this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2))
        ? this.collectionsApiService.getExcludedCommunicationRecipientRows(
            ...getExcludedArguments,
            this.store.selectSnapshot(CommonCollectionsState.getCollectionsMode),
          )
        : this.collectionsApiService.getExcludedCollectionsClients(...getExcludedArguments),
    ).pipe(
      map((excludedCollectionsClients: ClientGQL[] | CommunicationRecipientRow[]) => {
        if (isEmpty(excludedCollectionsClients)) {
          return [];
        }

        if ('id' in excludedCollectionsClients[0]) {
          return excludedCollectionsClients as CommunicationRecipientRow[];
        }

        const staffMembers = context.getState().staff.data;
        return CommonCollectionsState.addStaffNamesToClients(excludedCollectionsClients as ClientGQL[], staffMembers);
      }),
      tap<CommunicationRecipientRow[] | ClientGQLDisplay[]>({
        // Update the state with the response
        next: (response) =>
          context.patchState({
            excludedClients: PagedData.deserialize(context.getState().excludedClients)
              .updatePage(page, LoadableStatus.LOADED, response, context.getState().alreadyExcludedClientsTotal.data)
              .serialize(),
            latestExcludedClientsSort: sort,
          }),
        // Update the state with error for the page request
        error: (_) =>
          context.patchState({
            excludedClients: PagedData.deserialize(context.getState().excludedClients)
              .updatePage(page, LoadableStatus.ERROR, [])
              .serialize(),
            latestExcludedClientsSort: sort,
          }),
      }),
    );
  }

  @Action([SearchExcludedStatementRecipients])
  searchExcludedCollectionsClients(
    context: StateContext<CommonCollectionsStateModel>,
    { term }: SearchExcludedStatementRecipients,
  ): Observable<ClientGQLDisplay[] | CommunicationRecipientRow[]> | void {
    return this.handleSearch(
      context,
      term,
      context.getState().excludedClientSearchResults,
      (searchTerm) =>
        this.store.selectOnce(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2)).pipe(
          switchMap((isCollectionsV2Enabled) =>
            isCollectionsV2Enabled
              ? this.collectionsApiService.searchStatementRecipientRows(
                  searchTerm,
                  this.store.selectSnapshot(CommonCollectionsState.getLatestExcludedClientsSort),
                  this.store.selectSnapshot(CommonCollectionsState.getCollectionsMode),
                )
              : this.collectionsApiService.searchExcludedCollectionsClients(searchTerm),
          ),
          map((collectionsClients) => {
            if (this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2))) {
              return collectionsClients as CommunicationRecipientRow[];
            }

            const staff = this.store.selectSnapshot(CommonCollectionsState.getStaff);
            return CommonCollectionsState.addStaffNamesToClients(collectionsClients as ClientGQL[], staff);
          }),
        ),
      'excludedClientSearchResults',
    );
  }

  @Action([Clients_UpdateClientExclusion])
  updateClientCollectionsExclusion(
    { patchState }: StateContext<CommonCollectionsStateModel>,
    { clientId, excluded }: UpdateClientExclusion,
  ): void {
    const targetRecipient = this.store.selectSnapshot(CommonCollectionsState.getRawStatementRecipient(clientId));
    if (!targetRecipient) {
      return;
    }

    const updatedRecipient: StatementRecipientModel = CommonCollectionsState.isV1Recipient(targetRecipient)
      ? ({ ...targetRecipient, exclude_from_collections: excluded } as GraphQl.ClientCadenceType) // v1
      : ({ ...targetRecipient, enabled: !excluded } as SmartStatementRecipient); // v2

    const existingRecipients = this.store.selectSnapshot(CommonCollectionsState.getRawStatementRecipients);

    patchState({ recipients: CommonCollectionsState.coalesceRecipients(existingRecipients, [updatedRecipient]) });
  }

  //#endregion

  //#region v2

  @Action([GetExcludedStatementRecipientIds])
  getExcludedStatementRecipientIds(
    context: StateContext<CommonCollectionsStateModel>,
    { forceRefresh }: GetExcludedStatementRecipientIds,
  ): Observable<number[]> {
    const status = this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting('excludedRecipientIds', 'status'));
    if (!LoadableHelper.shouldRequest(status) && !forceRefresh) {
      return getLoadedResults(this.store.select(CommonCollectionsState.getCollectionsSetting('excludedRecipientIds')), true).pipe(take(1));
    }

    const collectionsMode = this.store.selectSnapshot(CommonCollectionsState.getCollectionsMode);
    return this.collectionsApiService.getExcludedCommunicationRecipientIds(collectionsMode).pipe(
      this._setCollectionsSettingLoadableInState(context, 'excludedRecipientIds', []),

      /**
       * If the excluded recipient ids are empty, set the default client limit and pages to avoid an unnecessary request when opening the
       * manage excluded recipients dialog.
       */
      tap((excludedRecipientIds) => {
        if (isEmpty(excludedRecipientIds)) {
          context.patchState({
            excludedClients: {
              limit: DEFAULT_RECIPIENT_PAGE_LIMIT,
              pages: [{ status: LoadableStatus.LOADED, data: { pageNumber: 1, data: [] } }],
              totalPages: 1,
            },
          });
        }
      }),
    );
  }

  /**
   * Update recipient locally. Only dispatched in v2.
   * @param patchState
   */
  @Action([ToggleSelectedRecipientEnabled])
  toggleSelectedRecipientEnabled({ patchState, dispatch }: StateContext<CommonCollectionsStateModel>): Observable<unknown> {
    const selectedRecipientId = this.store.selectSnapshot(CommonCollectionsState.getSelectedStatementRecipientId);
    const recipients = this.store.selectSnapshot(CommonCollectionsState.getRawStatementRecipients) as SmartStatementRecipient[];
    const selectedRecipientIndex = recipients.findIndex(({ id }) => id === selectedRecipientId);

    patchState({
      recipients: replaceArrayItem(recipients, selectedRecipientIndex, {
        ...recipients[selectedRecipientIndex],
        enabled: !recipients[selectedRecipientIndex].enabled,
      } as SmartStatementRecipient),
    });

    return dispatch(new GetRecipientPreviousStatements(selectedRecipientId, true));
  }

  //#endregion

  //#endregion

  //#region Message Settings

  /**
   * Get the message settings for the settings container message and addresses form
   */
  @Action([GetMessageSettings])
  getMessageSettings(
    context: StateContext<CommonCollectionsStateModel>,
    { cadenceId }: GetMessageSettings,
  ): Observable<[ActionDefinition<SmartStatementActionDefinitionJson>, SmartStatementBucketSetting[]]> | void {
    if (LoadableHelper.shouldRequest(context.getState().smartStatementBucketSettings.status)) {
      context.patchState({
        messageSettings: { status: LoadableStatus.LOADING, data: context.getState().messageSettings.data },
        smartStatementBucketSettings: { status: LoadableStatus.LOADING, data: context.getState().smartStatementBucketSettings.data },
      });

      return combineLatest([
        this.collectionsApiService.getSmartStatementActionDefinition(cadenceId).pipe(
          tap({
            next: (response) => context.patchState({ messageSettings: { status: LoadableStatus.LOADED, data: response } }),
            error: (_) =>
              context.patchState({
                messageSettings: {
                  status: LoadableStatus.ERROR,
                  data: context.getState().messageSettings.data,
                },
              }),
          }),
        ),
        this.collectionsApiService.getSmartStatementBucketSettings().pipe(
          tap({
            next: (response) => context.patchState({ smartStatementBucketSettings: { status: LoadableStatus.LOADED, data: response } }),
            error: (_) =>
              context.patchState({
                smartStatementBucketSettings: {
                  status: LoadableStatus.ERROR,
                  data: context.getState().smartStatementBucketSettings.data,
                },
              }),
          }),
        ),
      ]).pipe(take(1));
    }
  }

  /**
   * Update the Hasura action def for the message settings, and if successfully posted, update the state with the posted model
   */
  @Action([UpdateMessageSettings])
  updateMessageSettings(
    context: StateContext<CommonCollectionsStateModel>,
    { messageSettings, bucketSettings }: UpdateMessageSettings,
  ): Observable<unknown> {
    // Requires the settings to exist in order to update them
    if (
      !context.getState().smartStatementBucketSettings.data ||
      context.getState().messageSettingsUpdateRequestStatus === LoadableStatus.LOADING
    ) {
      return;
    }

    context.patchState({ messageSettingsUpdateRequestStatus: LoadableStatus.LOADING });

    return this.collectionsApiService.updateSmartStatementBucketSettings(messageSettings, bucketSettings).pipe(
      tap({
        next: () =>
          context.patchState({
            messageSettingsUpdateRequestStatus: LoadableStatus.LOADED,
            messageSettings: {
              status: LoadableStatus.LOADED,
              data: { ...context.getState().messageSettings.data, action_def_json: messageSettings },
            },
            smartStatementBucketSettings: { status: LoadableStatus.LOADED, data: bucketSettings },
          }),
        error: (_) => context.patchState({ messageSettingsUpdateRequestStatus: LoadableStatus.ERROR }),
      }),
    );
  }

  /**
   * Cache the dynamic fields used for message settings
   * @param context
   */
  @Action([GetMessageVariables])
  getMessageVariables(context: StateContext<CommonCollectionsStateModel>): Observable<unknown[]> | void {
    if (LoadableHelper.shouldRequest(context.getState().messageVariables.status)) {
      context.patchState({ messageVariables: { status: LoadableStatus.LOADING, data: context.getState().messageVariables.data } });

      return this.collectionsApiService
        .getDynamicFields()
        .pipe(setLoadableInState(context.patchState, 'messageVariables', context.getState().messageVariables.data));
    }
  }

  //#endregion

  //#region Global Misc. Data

  /**
   * Get lookup for matching the ID of the staff member that last updates a client's exclusion status.
   */
  @Action([GetStaff])
  getStaff({ patchState }: StateContext<CommonCollectionsStateModel>, { forceRefresh }: GetStaff): Observable<StaffGQL[] | LoadableStatus> {
    const status = this.store.selectSnapshot(CommonCollectionsState.getStaffStatus);
    if (!LoadableHelper.shouldRequest(status) && !forceRefresh) {
      return this.store.select(CommonCollectionsState.getStaffStatus).pipe(isLoaded(true), take(1));
    }

    patchState({ staff: { status: LoadableStatus.LOADING, data: [] } });

    return this.collectionsApiService.getStaff().pipe(setLoadableInState(patchState, 'staff'));
  }

  //#endregion

  //#region Action Helpers

  private handleSearch<T>(
    context: StateContext<CommonCollectionsStateModel>,
    searchTerm: string,
    searchResults: { term: string; results: Loadable<T>; requestedAt: number }[],
    searchFn: (term: string) => Observable<T>,
    stateKey: keyof CommonCollectionsStateModel,
  ): Observable<T> | void {
    const term = searchTerm.toLowerCase();
    const existingSearch = searchResults.find((search) => search.term === term);

    // Early return if we already have a valid search result
    if (!LoadableHelper.shouldRequest(existingSearch?.results?.status)) {
      return;
    }

    context.patchState({
      [stateKey]: [
        { term, requestedAt: new Date().getTime(), results: { status: LoadableStatus.LOADING } },
        ...searchResults.filter((search) => search.term !== term).slice(0, MAX_CACHED_SEARCHES),
      ],
    });

    return searchFn(term).pipe(
      tap({
        next: (response) =>
          context.patchState({
            [stateKey]: [
              {
                term,
                requestedAt: new Date().getTime(),
                results: { status: LoadableStatus.LOADED, data: response },
              },
              ...searchResults.filter((search) => search.term !== term),
            ],
          }),
        error: () =>
          context.patchState({
            [stateKey]: [
              { term, requestedAt: new Date().getTime(), results: { status: LoadableStatus.ERROR } },
              ...searchResults.filter((search) => search.term !== term),
            ],
          }),
      }),
    );
  }

  // Helper functions for common state updates
  private updateLoadableArrayItem<T>(array: Loadable<T>[], identity: number, status: LoadableStatus, data?: T): Loadable<T>[] {
    return [...array.filter((item) => item.identity !== identity), { identity, status, ...(data ? { data } : {}) }];
  }

  /*private updateClientInState(
    context: StateContext<CommonCollectionsStateModel>,
    clientId: number,
    updateFn: (client: StatementRecipient) => StatementRecipient,
  ): void {
    const targetClient = context.getState().recipients.find(({ client_id }) => client_id === clientId);
    if (targetClient) {
      context.patchState({
        recipients: CommonCollectionsState.coalesceRecipients(context.getState().recipients, [updateFn(targetClient)]),
      });
    }
  }*/

  private handleLoadableRequest<T>(
    context: StateContext<CommonCollectionsStateModel>,
    stateKey: keyof CommonCollectionsStateModel,
    request: Observable<T>,
    defaultValue?: T,
  ): Observable<T> {
    context.patchState({ [stateKey]: { status: LoadableStatus.LOADING, data: defaultValue } });

    return request.pipe(setLoadableInState(context.patchState, stateKey, defaultValue));
  }

  //#endregion

  //#region Settings v2

  private _setCollectionsSettingLoadableInState<Name extends keyof CollectionsSettingsStateModel, Data>(
    context: StateContext<CommonCollectionsStateModel>,
    loadableName: Name,
    valueOnError?: Data,
  ): MonoTypeOperatorFunction<Data> {
    return tap({
      next: (data) =>
        context.patchState({
          settings: {
            ...context.getState().settings,
            [loadableName]: { status: LoadableStatus.LOADED, data },
          },
        }),
      error: (_) =>
        context.patchState({
          settings: {
            ...context.getState().settings,
            [loadableName]: { status: LoadableStatus.ERROR, data: valueOnError },
          },
        }),
    });
  }

  @Action([GetCollectionsSettings])
  getCollectionsSettings(
    _: StateContext<CommonCollectionsStateModel>,
    { forceRefresh, fields }: GetCollectionsSettings,
  ): Observable<unknown | LoadableStatus> {
    if (isEmpty(fields)) {
      return of(LoadableStatus.LOADED);
    }

    const updateStatus = this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting('updateStatus'));
    if (!LoadableHelper.shouldRequest(updateStatus)) {
      return this.store.select(CommonCollectionsState.getCollectionsSetting('updateStatus')).pipe(isLoaded(true), take(1));
    }

    const statuses = fields.map((field) => this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting(field, 'status')));

    const status = LoadableHelper.coalesceStatus(statuses);
    if (!LoadableHelper.shouldRequest(status) && !forceRefresh) {
      const statuses$ = fields.map((field) => this.store.select(CommonCollectionsState.getCollectionsSetting(field, 'status')));
      return combineLatest(statuses$).pipe(
        map((statuses2) => LoadableHelper.coalesceStatus(statuses2)),
        isLoaded(true),
        take(1),
      );
    }

    const actions = []
      .concat(fields.includes('throttling') ? new GetThrottlingSettings(forceRefresh) : [])
      .concat(fields.includes('triggers') ? new GetTriggerSettings(forceRefresh) : [])
      .concat(fields.includes('autoApprove') ? new GetAutoApproveSetting(forceRefresh) : [])
      .concat(fields.includes('email') ? new GetEmailSettings(forceRefresh) : [])
      .concat(fields.includes('excludedRecipientIds') ? new GetExcludedStatementRecipientIds(forceRefresh) : []);

    return this.store.dispatch(actions);
  }

  @Action([GetThrottlingSettings])
  getThrottlingSettings(
    context: StateContext<CommonCollectionsStateModel>,
    { forceRefresh }: GetThrottlingSettings,
  ): Observable<unknown | LoadableStatus> {
    const status = this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting('throttling', 'status'));
    if (!LoadableHelper.shouldRequest(status) && !forceRefresh) {
      return this.store.select(CommonCollectionsState.getCollectionsSetting('throttling', 'status')).pipe(isLoaded(true), take(1));
    }

    return this.collectionsApiService.getThrottlingSettings().pipe(this._setCollectionsSettingLoadableInState(context, 'throttling'));
  }

  @Action([GetTriggerSettings])
  getTriggerSettings(
    context: StateContext<CommonCollectionsStateModel>,
    { forceRefresh }: GetTriggerSettings,
  ): Observable<unknown | LoadableStatus> {
    const status = this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting('triggers', 'status'));
    if (!LoadableHelper.shouldRequest(status) && !forceRefresh) {
      return this.store.select(CommonCollectionsState.getCollectionsSetting('triggers', 'status')).pipe(isLoaded(true), take(1));
    }

    return this.collectionsApiService.getTriggersSettings().pipe(this._setCollectionsSettingLoadableInState(context, 'triggers'));
  }

  @Action([GetAutoApproveSetting])
  getAutoApproveSetting(
    context: StateContext<CommonCollectionsStateModel>,
    { forceRefresh }: GetAutoApproveSetting,
  ): Observable<unknown | LoadableStatus> {
    const status = this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting('autoApprove', 'status'));
    if (!LoadableHelper.shouldRequest(status) && !forceRefresh) {
      return this.store.select(CommonCollectionsState.getCollectionsSetting('autoApprove', 'status')).pipe(isLoaded(true), take(1));
    }

    return this.collectionsApiService.getAutoApproveSettings().pipe(this._setCollectionsSettingLoadableInState(context, 'autoApprove'));
  }

  @Action([GetEmailSettings])
  getEmailSettings(
    context: StateContext<CommonCollectionsStateModel>,
    { forceRefresh }: GetEmailSettings,
  ): Observable<unknown | LoadableStatus> {
    const status = this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting('email', 'status'));
    if (!LoadableHelper.shouldRequest(status) && !forceRefresh) {
      return this.store.select(CommonCollectionsState.getCollectionsSetting('email', 'status')).pipe(isLoaded(true), take(1));
    }

    return this.collectionsApiService.getEmailSettings().pipe(this._setCollectionsSettingLoadableInState(context, 'email'));
  }

  @Action([UpdateCollectionsSettings])
  updateCollectionsSettings(
    context: StateContext<CommonCollectionsStateModel>,
    { payload }: UpdateCollectionsSettings,
  ): Observable<unknown | LoadableStatus> {
    const status = this.store.selectSnapshot(CommonCollectionsState.getCollectionsSetting('updateStatus'));
    if (status === LoadableStatus.LOADING) {
      return this.store.select(CommonCollectionsState.getCollectionsSetting('updateStatus')).pipe(isLoaded(true), take(1));
    }

    context.patchState({ settings: { ...context.getState().settings, updateStatus: LoadableStatus.LOADING } });

    return this.collectionsApiService.updateCollectionsSettings(payload).pipe(
      map((_) => LoadableStatus.LOADED),
      this._setCollectionsSettingLoadableInState(context, 'updateStatus', LoadableStatus.ERROR),
      switchMap((_) => this.store.dispatch(new GetCollectionsSettings(true, CommonCollectionsState.getUpdateSettingsFields(payload)))),
    );
  }

  static getUpdateSettingsFields(payload: UpdateCollectionsSettingsPayload): UpdatableSettingsField[] {
    return []
      .concat(payload.throttleDays || payload.throttleEnabled != undefined ? ['throttling'] : [])
      .concat(payload.autoApprove != undefined ? ['autoApprove'] : [])
      .concat(
        payload.expressionList ||
          payload.noTriggerStatementEnabled != undefined ||
          payload.noTriggerStatementDay ||
          payload.noTriggerStatementOccurrence
          ? ['triggers']
          : [],
      )
      .concat(payload.emailSettings ? ['email'] : [])
      .concat(payload.exclusionList ? ['excludedRecipientIds'] : []);
  }

  //#endregion
}
